import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useGetClientOverview } from '@/domain/client';
import type { ImagesFishGroupScope } from '@/domain/images';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { applyDemoMaskToOverviewLocation } from '@/utils/demoMasking';
import { resolvePenCohortFishGroupId, resolvePenCohortName } from '@/utils/resolvePenCohortName';

import { useCageOptional } from './cage';
import { readSelectedFishGroupId } from './cage-filter-context';
import { useDemoMode } from './demo/DemoContext';

/**
 * Which fish group a pen page's data requests are about: the `fg` param, else the group in the
 * pen now; `fishGroupId` undefined once resolved means the whole cage (flag off, empty pen).
 *
 * `isResolved` is false while that answer is unknown: the flag, or the client overview the
 * active group comes from, has not loaded. A request waiting on it fires once, scoped, instead of
 * cage-wide first and scoped again a moment later. If the overview fails, the answer stays
 * unknown and `error`/`refetch` are that lookup's: a failure is not an empty pen, so nothing
 * falls back to the whole cage and its other groups' data.
 */
export type PenCohortFishGroup = ImagesFishGroupScope;

const WHOLE_CAGE: PenCohortFishGroup = {
    isResolved: true,
    error: null,
    isFetching: false,
    refetch: () => undefined,
};

interface PenCohort {
    name?: string;
    fishGroup: PenCohortFishGroup;
}

/**
 * The cohort the current pen's pages are about: its name for the title, and its id for the
 * requests that have to be scoped to it.
 *
 * A context rather than a hook called where it is needed, because the name's one
 * consumer is `PageHeader` — the title row `PageLayout` renders on every page in the
 * portal, including pages with no Router ancestor in their tests. Resolving it
 * here, inside the cage route tree where `useLocation` and the query client
 * are guaranteed, is what lets that header read it with a plain `useContext`
 * that cannot throw.
 *
 * Outside `PenCohortProvider`, which is the case everywhere except cage pages, there is no
 * name and the id resolves to the whole cage.
 */
const PenCohortContext = createContext<PenCohort>({ fishGroup: WHOLE_CAGE });

/** The current pen's cohort name. Safe anywhere — `undefined` off cage pages. */
export function usePenCohortName(): string | undefined {
    return useContext(PenCohortContext).name;
}

/**
 * The fish group to scope a pen page's requests to, for requests that cannot take the `fg`
 * param alone because "no `fg`" has to mean the group in the pen now rather than the cage's whole
 * history (images). Safe anywhere — the whole cage, resolved, off cage pages.
 */
export function usePenCohortFishGroup(): PenCohortFishGroup {
    return useContext(PenCohortContext).fishGroup;
}

export interface PenCohortProviderProps {
    children: ReactNode;
}

/**
 * Resolves the pen's cohort from the client overview — the same source and the
 * same helper the breadcrumb uses, so the title and the breadcrumb cannot name
 * different groups for the same pen.
 *
 * The alternative source was the cage overview's own `filter.fishGroup`, which
 * would report the window the backend actually resolved rather than what the
 * overview believes. That is a stronger guarantee, and that field does not exist
 * yet — adding it is a backend change. For naming a pen it is a fair trade.
 *
 * Must be mounted inside `CageProvider`; `CagePageWrapper` is the only mount.
 */
export function PenCohortProvider(props: PenCohortProviderProps) {
    const { children } = props;
    const cageContext = useCageOptional();
    const routerLocation = useLocation();
    const { isDemoMode } = useDemoMode();
    const { t } = useTranslation();

    const fishGroupsFlag = usePostHogFeatureFlag('fish-group-tracking');

    const clientId = cageContext?.client?.id;
    const locationId = cageContext?.location?.id;
    const cageId = cageContext?.cageId;

    // Shares the breadcrumb's cached query rather than opening a second one:
    // same key, and `AppShell` has almost always resolved it already.
    const {
        data: clientOverview,
        error: clientOverviewError,
        isFetching: clientOverviewFetching,
        refetch: refetchClientOverview,
    } = useGetClientOverview(clientId ?? 0, {
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        enabled: clientId != null,
    });

    const overviewLocation = useMemo(() => {
        const found = clientOverview?.locations?.find(
            (candidate) => candidate.locationId === locationId
        );
        if (!found) return undefined;
        // Masked the same way the breadcrumb masks it, so a demo session cannot
        // pick up a real cohort name through the page title.
        return isDemoMode ? applyDemoMaskToOverviewLocation(found, t) : found;
    }, [clientOverview?.locations, locationId, isDemoMode, t]);

    const urlFishGroupId = readSelectedFishGroupId(routerLocation.search);

    // The `fg` param first, so a closed cohort reached by link is named here too
    // rather than the page claiming whoever occupies the pen today.
    const name = resolvePenCohortName(overviewLocation, cageId, urlFishGroupId);

    // Gated like every request's `fg`: with the flag off nothing is scoped, whatever the URL says.
    const selectedFishGroupId = fishGroupsFlag.enabled ? urlFishGroupId : undefined;
    const fishGroupId = fishGroupsFlag.enabled
        ? resolvePenCohortFishGroupId(overviewLocation, cageId, selectedFishGroupId)
        : undefined;
    const isResolved =
        selectedFishGroupId != null ||
        (!fishGroupsFlag.isLoading && (!fishGroupsFlag.enabled || clientOverview != null));
    // Only a lookup the answer depends on can fail it: not with `fg`, not with the flag off or
    // still loading, and not once the overview has arrived.
    const lookupError = !isResolved && fishGroupsFlag.enabled ? clientOverviewError : null;

    const cohort = useMemo<PenCohort>(
        () => ({
            name,
            fishGroup: {
                fishGroupId,
                isResolved,
                error: lookupError,
                isFetching: clientOverviewFetching,
                refetch: refetchClientOverview,
            },
        }),
        [name, fishGroupId, isResolved, lookupError, clientOverviewFetching, refetchClientOverview]
    );

    return <PenCohortContext value={cohort}>{children}</PenCohortContext>;
}
